/*
 * The job page's money cards, like Jobber's job (owner, 2026-10-31): Billing (how it bills, automatic payments and its
 * invoices), Expenses (what the office spent on it), Labor (each person's time and pay on its done visits) and Profit.
 * Labor and Profit carry pay, so the server only sends them to people who see payroll.
 */
import { useNavigate } from "@tanstack/react-router";
import { billingWords, toCents, type JobCosts, type JobDetail, type JobExpenseRow, type JobTimeRow } from "@steward/shared";
import { useState, type SubmitEvent } from "react";
import {
  useAddJobExpense,
  useChangeJobExpense,
  useJobCosts,
  useRemoveJobExpense,
  useRemoveVisitTime,
  useRestoreJobExpense,
  useRestoreVisitTime,
  useUpdateJob,
} from "../api/hooks";
import { useInvoices } from "../api/money";
import { useSession } from "../app/session";
import { InputText } from "../components/fields";
import { minutesWords, momentIn, moneyExact, shortDate, todayIn } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { Button, Card, Details, IconButton, LoadError, Skeleton, StatusLabel, Switch } from "../components/parts";
import { errText } from "../components/work";
import { Pager, PAGE } from "./ClientCards";
import { InvoiceStatus } from "./invoices/common";

/** The client's card on file in a few words (owner, Oct 31), so the office does not call to ask for one. */
export function cardLine(card: JobDetail["card"], today: string): string {
  if (!card) return "No card on file";
  const [y, m] = today.split("-").map(Number);
  const old = card.expYear < (y ?? 0) || (card.expYear === y && card.expMonth < (m ?? 0));
  const exp = `${String(card.expMonth).padStart(2, "0")}/${String(card.expYear).slice(-2)}`;
  return `${card.brand} ending in ${card.last4}, ${old ? "expired" : "expires"} ${exp}${card.byPhone ? ", taken by phone" : ""}`;
}

const AUTO_WORDS: Record<JobDetail["autoCharge"], string> = {
  on: "On. The saved card is charged when an invoice is ready.",
  job_off: "Off for this job.",
  client_off: "Off. The client has not said yes to auto charge.",
  no_card: "Off. The client has no card saved.",
  one_time: "Off. One time jobs are never charged by themselves.",
};

export function JobBilling({ j }: { j: JobDetail }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const update = useUpdateJob();
  const [at, setAt] = useState(0);
  const invoices = useInvoices(company.id, { state: "all", jobId: j.id });
  const rows = invoices.data?.invoices ?? [];
  const balance = rows.reduce((s, i) => s + i.balanceCents, 0);
  const setAuto = (autoPay: boolean) =>
    update.mutate(
      { id: j.id, patch: { autoPay } },
      {
        onSuccess: () => toast(autoPay ? "Automatic payments on for this job" : "Automatic payments off for this job"),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Card
      title="Billing"
      flush
      action={
        can("invoice.update") && j.active ? (
          <Button
            type="secondary"
            size="small"
            onClick={() => void navigate({ to: "/invoices", search: { new: true, clientId: j.clientId } })}
          >
            New invoice
          </Button>
        ) : null
      }
    >
      <div className="job-bill-facts">
        <div>
          <span className="muted small">Frequency</span>
          <b>{billingWords(j.billing)}</b>
        </div>
        <div>
          <span className="muted small">Billing type</span>
          <b>{j.billing === "visit" ? "Visit based" : j.billing === "outside" ? "Not billed here" : "Monthly"}</b>
        </div>
        <div>
          <span className="muted small">Automatic payments</span>
          <span className="row">
            {j.frequency !== "one-time" && can("job.update") ? (
              <Switch
                label="Automatic payments for this job"
                checked={j.autoPay}
                onChange={setAuto}
                disabled={update.isPending}
              />
            ) : null}
            <b>{AUTO_WORDS[j.autoCharge]}</b>
          </span>
        </div>
        {j.card !== undefined ? (
          <div>
            <span className="muted small">Card on file</span>
            <b>{cardLine(j.card, todayIn(company.timezone))}</b>
          </div>
        ) : null}
      </div>
      {invoices.error ? (
        <LoadError error={invoices.error} retry={() => void invoices.refetch()} />
      ) : invoices.isLoading ? (
        <Skeleton rows={2} />
      ) : rows.length ? (
        <>
          <table className="data bill-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Due</th>
                <th>Status</th>
                <th>Subject</th>
                <th className="right">Total</th>
                <th className="right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(at, at + PAGE).map((i) => (
                <tr key={i.id} className="clickable" onClick={() => void navigate({ to: "/invoices/$id", params: { id: i.id } })}>
                  <td>
                    <b>{i.number ? `#${String(i.number)}` : "Not sent"}</b>
                  </td>
                  <td className="num">{i.dueAt ? shortDate(i.dueAt.slice(0, 10)) : "None"}</td>
                  <td>
                    <InvoiceStatus i={i} />
                  </td>
                  <td className="muted">{i.subject}</td>
                  <td className="right num">{moneyExact(i.totalCents)}</td>
                  <td className="right num">{moneyExact(i.balanceCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5}>
                  <b>Still owed on this job</b>
                </td>
                <td className="right num">
                  <b className={balance > 0 ? "owed" : undefined}>{moneyExact(balance)}</b>
                </td>
              </tr>
            </tfoot>
          </table>
          <Pager at={at} total={rows.length} go={setAt} />
        </>
      ) : (
        <div className="card-empty muted">No invoices for this job yet.</div>
      )}
    </Card>
  );
}

export function JobExpenses({ j, costs }: { j: JobDetail; costs: ReturnType<typeof useJobCosts> }) {
  const { can } = useSession();
  const [editing, setEditing] = useState<JobExpenseRow | "new" | null>(null);
  const [at, setAt] = useState(0);
  const editable = can("job.update");
  const rows = costs.data?.expenses ?? [];
  return (
    <Card
      title="Expenses"
      flush
      action={
        editable ? (
          <IconButton icon="plus" label="Add an expense" className="iconbtn card-add" onClick={() => setEditing("new")} />
        ) : null
      }
    >
      {costs.error ? (
        <LoadError error={costs.error} retry={() => void costs.refetch()} />
      ) : costs.isLoading ? (
        <Skeleton rows={2} />
      ) : rows.length ? (
        <>
          <table className="data bill-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Added by</th>
                <th>Date</th>
                <th className="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(at, at + PAGE).map((e) => (
                <tr
                  key={e.id}
                  className={editable ? "clickable" : undefined}
                  onClick={editable ? () => setEditing(e) : undefined}
                >
                  <td>
                    <span className="two-line">
                      <b>{e.item}</b>
                      {e.details ? <small className="muted">{e.details}</small> : null}
                    </span>
                  </td>
                  <td className="muted">{e.addedBy ?? ""}</td>
                  <td className="num">{shortDate(e.spentOn)}</td>
                  <td className="right num">{moneyExact(e.amountCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3}>
                  <b>Total</b>
                </td>
                <td className="right num">
                  <b>{moneyExact(costs.data?.expenseCents ?? 0)}</b>
                </td>
              </tr>
            </tfoot>
          </table>
          <Pager at={at} total={rows.length} go={setAt} />
        </>
      ) : (
        <div className="card-empty muted">Keep track of what you spend on this job, like supplies or a dump fee.</div>
      )}
      {editing ? <ExpenseForm j={j} row={editing === "new" ? null : editing} onClose={() => setEditing(null)} /> : null}
    </Card>
  );
}

function ExpenseForm({ j, row, onClose }: { j: JobDetail; row: JobExpenseRow | null; onClose: () => void }) {
  const { company } = useSession();
  const toast = useToast();
  const add = useAddJobExpense();
  const change = useChangeJobExpense();
  const remove = useRemoveJobExpense();
  const restore = useRestoreJobExpense();
  const [item, setItem] = useState(row?.item ?? "");
  const [details, setDetails] = useState(row?.details ?? "");
  const [spentOn, setSpentOn] = useState(row?.spentOn ?? todayIn(company.timezone));
  const [amount, setAmount] = useState(row ? (row.amountCents / 100).toFixed(2) : "");
  const [error, setError] = useState<string | null>(null);
  const busy = add.isPending || change.isPending || remove.isPending;
  const save = (e?: SubmitEvent<HTMLFormElement>) => {
    e?.preventDefault();
    const n = Number(amount.replace(/[$,\s]/g, ""));
    if (!item.trim()) {
      setError("Name the expense.");
      return;
    }
    if (!Number.isFinite(n) || n <= 0) {
      setError("Type the amount, like 24.50.");
      return;
    }
    const body = { item: item.trim(), details: details.trim() || null, spentOn, amountCents: toCents(n) };
    const done = {
      onSuccess: () => {
        toast(row ? "Expense saved" : "Expense added");
        onClose();
      },
      onError: (x: unknown) => setError(errText(x)),
    };
    if (row) change.mutate({ id: row.id, patch: body }, done);
    else add.mutate({ jobId: j.id, body }, done);
  };
  const drop = () => {
    if (!row) return;
    remove.mutate(row.id, {
      onSuccess: () => {
        toast("Expense removed", { undo: () => restore.mutate(row.id) });
        onClose();
      },
      onError: (x) => setError(errText(x)),
    });
  };
  return (
    <Modal
      title={row ? "Change expense" : "Add an expense"}
      open
      onClose={onClose}
      footer={
        <div className="row expense-foot">
          {row ? (
            <Button type="secondary" variation="destructive" onClick={drop} loading={remove.isPending}>
              Remove
            </Button>
          ) : (
            <span />
          )}
          <div className="row">
            <Button type="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => save()} loading={busy}>
              Save
            </Button>
          </div>
        </div>
      }
    >
      <form className="form-grid" onSubmit={save}>
        <InputText label="Item" value={item} onChange={(e) => setItem(e.target.value)} placeholder="Supplies" wide autoFocus />
        <InputText label="Details" value={details} onChange={(e) => setDetails(e.target.value)} wide />
        <InputText label="Date" type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
        <InputText label="Amount" prefix="$" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        {error ? <p className="error wide">{error}</p> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

export function JobLabor({ costs }: { costs: JobCosts }) {
  const navigate = useNavigate();
  const [at, setAt] = useState(0);
  const rows = costs.labor ?? [];
  const minutes = rows.reduce((a, l) => a + l.minutes, 0);
  const pay = rows.reduce((a, l) => a + l.payCents, 0);
  return (
    <Card title="Labor" flush>
      {rows.length ? (
        <>
          <table className="data bill-table">
            <thead>
              <tr>
                <th>Team</th>
                <th>Visit</th>
                <th className="right">Time</th>
                <th className="right">Pay</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(at, at + PAGE).map((l) => (
                <tr
                  key={`${l.visitId}.${l.person.id}`}
                  className="clickable"
                  onClick={() => void navigate({ to: "/visits/$ref", params: { ref: l.visitId } })}
                >
                  <td>
                    <b>{l.person.name}</b>
                  </td>
                  <td className="num">{shortDate(l.date)}</td>
                  <td className="right num">
                    {l.minutes ? minutesWords(l.minutes) : <span className="muted">No time kept</span>}
                  </td>
                  <td className="right num">{moneyExact(l.payCents)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>
                  <b>Total</b>
                </td>
                <td className="right num">
                  <b>{minutesWords(minutes)}</b>
                </td>
                <td className="right num">
                  <b>{moneyExact(pay)}</b>
                </td>
              </tr>
            </tfoot>
          </table>
          <Pager at={at} total={rows.length} go={setAt} />
          <p className="card-foot muted small">
            From done visits. Time is the visit timer. Pay uses each person's pay in HR, before tips.
          </p>
        </>
      ) : (
        <div className="card-empty muted">Labor shows here once a visit is marked done.</div>
      )}
    </Card>
  );
}

/**
 * A visit counts as long when it took twice the job's usual time and an hour more, like a timer left on overnight. Usual is
 * the middle of the timed visits, so one long timer does not hide itself by pulling the average up.
 */
export function looksLong(minutes: number, all: readonly number[]): boolean {
  const timed = all.filter((m) => m > 0).sort((a, b) => a - b);
  if (timed.length < 3) return false;
  const usual = timed[Math.floor((timed.length - 1) / 2)] ?? 0;
  return minutes >= usual * 2 && minutes - usual >= 60;
}

/**
 * How long the job takes, start to finish (owner, 2026-11-03): the average of its done visits on the timer, what they
 * bill per hour, and the time log. A timer someone forgot to stop is removed, not edited; Undo puts it back.
 */
export function JobTimeLog({ j, costs }: { j: JobDetail; costs: ReturnType<typeof useJobCosts> }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const remove = useRemoveVisitTime();
  const restore = useRestoreVisitTime();
  const [at, setAt] = useState(0);
  const [removing, setRemoving] = useState<JobTimeRow | null>(null);
  const t = costs.data?.time;
  const rows = t?.log ?? [];
  const editable = can("visit.update");
  const clockAt = (iso: string | null) => (iso ? momentIn(iso, company.timezone).time.replace(/ [A-Z]{2,5}$/, "") : "");
  const all = rows.map((r) => r.minutes);
  const planned = j.hours ? Math.round(j.hours * 60) : null;
  const confirm = () => {
    if (!removing) return;
    const row = removing;
    remove.mutate(row.visitId, {
      onSuccess: () => {
        setRemoving(null);
        toast(`Time removed from ${shortDate(row.date)}`, {
          undo: () => restore.mutate(row.visitId, { onError: (e) => toast(errText(e), { kind: "error" }) }),
        });
      },
      onError: (e) => toast(errText(e), { kind: "error" }),
    });
  };
  return (
    <Card title="Time" flush>
      {costs.error ? (
        <LoadError error={costs.error} retry={() => void costs.refetch()} />
      ) : costs.isLoading || !t ? (
        <Skeleton rows={2} />
      ) : (
        <>
          <div className="job-bill-facts time-facts">
            <div>
              <span className="muted small">Average visit</span>
              <b>{t.avgMinutes !== null ? minutesWords(t.avgMinutes) : "No time kept yet"}</b>
            </div>
            <div>
              <span className="muted small">Planned</span>
              <b>{planned ? minutesWords(planned) : "Not set"}</b>
            </div>
            <div>
              <span className="muted small">Billed per hour</span>
              <b>{t.perHourCents !== null ? moneyExact(t.perHourCents) : "Not yet"}</b>
            </div>
            <div>
              <span className="muted small">Visits timed</span>
              <b>{String(t.timedVisits)}</b>
            </div>
          </div>
          {rows.length ? (
            <>
              <table className="data bill-table time-log">
                <thead>
                  <tr>
                    <th>Visit</th>
                    <th>Start to finish</th>
                    <th>Started by</th>
                    <th className="right">Time</th>
                    {editable ? <th aria-label="Remove" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(at, at + PAGE).map((r) => (
                    <tr
                      key={r.visitId}
                      className="clickable"
                      onClick={() => void navigate({ to: "/visits/$ref", params: { ref: r.visitId } })}
                    >
                      <td className="num">{shortDate(r.date)}</td>
                      <td className="num">
                        {r.running ? (
                          <span className="row">
                            {clockAt(r.startedAt)} <StatusLabel status="informative">Running</StatusLabel>
                          </span>
                        ) : r.startedAt ? (
                          `${clockAt(r.startedAt)} to ${clockAt(r.stoppedAt)}`
                        ) : (
                          <span className="muted">No clock times</span>
                        )}
                      </td>
                      <td className="muted">{r.startedBy ?? ""}</td>
                      <td className="right num">
                        <span className="row time-log-time">
                          {looksLong(r.minutes, all) ? <StatusLabel status="warning">Looks long</StatusLabel> : null}
                          {r.minutes ? minutesWords(r.minutes) : r.running ? "" : "0 min"}
                        </span>
                      </td>
                      {editable ? (
                        <td className="right" onClick={(e) => e.stopPropagation()}>
                          <IconButton icon="trash" label="Remove this time" size={18} onClick={() => setRemoving(r)} />
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager at={at} total={rows.length} go={setAt} />
              <p className="card-foot muted small">
                From the visit timer. Average and per hour count done visits only. Remove a time if the timer was left on.
              </p>
            </>
          ) : (
            <div className="card-empty muted">Start the timer on a visit and its time shows here.</div>
          )}
        </>
      )}
      <Modal
        title="Remove this time?"
        open={removing !== null}
        onClose={() => setRemoving(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button variation="destructive" loading={remove.isPending} onClick={confirm}>
              Remove
            </Button>
          </>
        }
      >
        {removing ? (
          <p>
            {shortDate(removing.date)} had {removing.minutes ? minutesWords(removing.minutes) : "a running timer"}. It comes off
            the average and the labor for this job. Payroll does not change.
          </p>
        ) : null}
      </Modal>
    </Card>
  );
}

export function JobProfit({ costs }: { costs: JobCosts }) {
  const p = costs.profit;
  if (!p) return null;
  return (
    <Card title="Profit">
      <Details
        rows={[
          ["Done visits", String(p.visits)],
          ["Billed", moneyExact(p.revenueCents)],
          ["Labor", `- ${moneyExact(p.laborCents)}`],
          ["Expenses", `- ${moneyExact(p.expenseCents)}`],
          [
            <b key="l">Profit</b>,
            <b key="v" className={p.profitCents < 0 ? "owed" : undefined}>
              {moneyExact(p.profitCents)}
              {p.marginPercent !== null ? ` (${String(p.marginPercent)}%)` : ""}
            </b>,
          ],
        ]}
      />
    </Card>
  );
}
